import { useQuery } from '@tanstack/react-query';
import { RATING_KIND_MAP, type RatingKind } from '@goyone/shared';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import { Icon } from './Icon';

/**
 * آراء العميل في ملفّه — عبر كل السياقات.
 *
 * كل تقييمٍ تركه هذا العميل (خدمة، صفقة، عقد، مشروع) يظهر هنا بنجومه
 * وتعليقه وسياقه — فمن يفتح الملفّ يعرف رأي صاحبه لا انطباع الفريق
 * عنه فقط. والمُرسَل الذي لم يُجَب يُعدّ بصدق لا يُخفى.
 */

interface RatingRow {
  id: string;
  kind: RatingKind;
  score: number | null;
  comment: string | null;
  createdAt: string;
  submittedAt: string | null;
}

export function AccountRatings({ accountId }: { accountId: string }) {
  const ratings = useQuery({
    queryKey: ['ratings', 'account', accountId],
    queryFn: () => api<{ items: RatingRow[] }>(`/ratings/by-account/${accountId}`),
  });

  const items = ratings.data?.items ?? [];
  const answered = items.filter((r) => r.score != null);
  const pending = items.length - answered.length;

  /* لا طلبات أصلًا: لا بطاقة فارغة تشغل الملف — الزرّ في رأس الصفحة. */
  if (ratings.isSuccess && items.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">آراء العميل</h2>
        {pending > 0 && (
          <span className="tiny mute">{pending} بانتظار الردّ</span>
        )}
      </div>
      <div className="card-body">
        {ratings.isPending && <div className="skeleton" style={{ height: 80 }} />}

        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          {answered.map((r) => (
            <div key={r.id} className="cc-review">
              <div className="row" style={{ gap: 'var(--sp-2)' }}>
                <span className="cc-stars" aria-label={`${r.score} من ٥`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n} className={`cc-star${(r.score ?? 0) >= n ? ' on' : ''}`}>
                      <Icon name="star" size={14} />
                    </span>
                  ))}
                </span>
                <span className="tiny mute">{RATING_KIND_MAP[r.kind]?.label ?? r.kind}</span>
                <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
                  {r.submittedAt ? relative(r.submittedAt) : ''}
                </span>
              </div>
              {r.comment && <p className="small cc-feedback">{r.comment}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
